import { sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * PDP の Recently viewed セクション（ヨコモ専用・#209）。
 *
 * 閲覧履歴はサーバー側に無く localStorage（`viewedProducts`）にしか無いため、
 * Liquid だけでは描画できない。既存の検索ドロワーと同じ手法で、
 * 「商品IDの検索クエリ（`id:1 OR id:2`）を付けた Section Rendering API」で
 * 自分自身を取り直し、中身を差し替える。
 *
 * ⚠️ 現在見ている商品も履歴に入る（`snippets/scripts.liquid`）ので、ここで除外する。
 *    そのぶん保存側（`RecentlyViewed.#MAX_PRODUCTS`）を 7 に増やしてあり、
 *    除外後にちょうど #MAX_DISPLAY 件が残る。
 */
class RecentlyViewedYokomo extends HTMLElement {
  /**
   * @static @constant {number} 並べるカードの最大数のフォールバック。
   * 値の持ち主は `sections/recently-viewed-yokomo.liquid` の `max_display`
   * （器の枚数と揃える必要があるため）。`data-max-display` で渡ってくる。
   */
  static #MAX_DISPLAY = 6;

  /**
   * ページ下部のセクションなので、近づくまで取りに行かない
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecentlyViewed();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  connectedCallback() {
    // 履歴が空・必須属性が無い＝カードは出せないので、通信を待たずに器を捨てる。
    // 画面に近づくまで高さを予約したままにすると、その時点で縮んで
    // レイアウトシフトになる（#438）。
    if (!this.dataset.sectionId || !this.id || this.#getProductIds().length === 0) {
      this.#clearPlaceholders();
      return;
    }

    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
  }

  /**
   * 表示する商品IDを履歴から取り出す。
   * @returns {string[]} 取れなければ空配列（localStorage が壊れていても投げない）
   */
  #getProductIds() {
    try {
      const max = Number(this.dataset.maxDisplay) || RecentlyViewedYokomo.#MAX_DISPLAY;
      return RecentlyViewed.getProducts()
        .filter((id) => id !== this.dataset.excludeId)
        .slice(0, max);
    } catch (error) {
      console.error('Recently viewed products error:', error);
      return [];
    }
  }

  /**
   * カードの高さを予約していた器（`.card-scroll-section-yokomo__placeholder`）を捨てる。
   * カードが出せないと分かった時点で呼ぶ。置いたままにすると見出しと空の行だけが残る。
   * 差し替え済み（カードが入っている）ときは何もしない。
   */
  #clearPlaceholders() {
    if (!this.querySelector('.card-scroll-section-yokomo__placeholder')) return;
    this.innerHTML = '';
  }

  async #loadRecentlyViewed() {
    const { sectionId } = this.dataset;

    // Section Rendering API は自分自身を返すので、差し替え済みなら二度取らない
    if (this.dataset.loaded === 'true') return;

    try {
      const productIds = this.#getProductIds();
      if (productIds.length === 0) {
        this.#clearPlaceholders();
        return;
      }

      const url = new URL(Theme.routes.search_url, location.origin);
      url.searchParams.set('q', productIds.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('resources[type]', 'product');

      // 履歴は閲覧のたびに変わるのでキャッシュは使わない
      const html = await sectionRenderer.getSectionHTML(sectionId, false, url);
      const section = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(`recently-viewed-yokomo[id="${this.id}"]`);

      if (!section || !section.innerHTML.trim()) {
        this.#clearPlaceholders();
        return;
      }

      this.innerHTML = section.innerHTML;
      this.dataset.loaded = 'true';
    } catch (error) {
      // 履歴が出せなくても商品ページの用は足りるので、セクションを空にする
      this.#clearPlaceholders();
      console.error('Recently viewed products error:', error);
    }
  }
}

if (!customElements.get('recently-viewed-yokomo')) {
  customElements.define('recently-viewed-yokomo', RecentlyViewedYokomo);
}
